/**
 * ForumDiscussionCard — Card component for LitClub discussions
 *
 * Layout matches the LitDigests/LitHub card pattern:
 * 1. Forum Specialty/Subspecialty header
 * 2. Latest Post info (title, author, time)
 * 3. Truncated preview of 2-3 recent comments (collapsible)
 * 4. Action buttons
 */
import React, { useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  ChevronDown,
  ChevronUp,
  MessageSquare,
  Users,
  Clock,
  ChevronRight,
  Eye,
  Lock,
  Plus,
  Sparkles,
} from 'lucide-react';

const CARD_BG = [
  'bg-white',
  'bg-slate-50/70',
  'bg-gray-50/60',
  'bg-zinc-50/50',
];

interface ForumThread {
  thread_id: string;
  title: string;
  created_by: string;
  creator_name?: string;
  creator_is_verified?: boolean;
  last_activity_at?: string;
  comment_count?: number;
  preview_comment?: string;
  is_pinned?: boolean;
}

interface ForumDiscussionCardProps {
  specialty_id: string;
  specialty_name: string;
  subspecialty_name?: string;
  thread_count: number;
  member_count: number;
  last_activity?: string;
  threads: ForumThread[];
  index?: number;
  can_enter?: boolean;
  can_post?: boolean;
  access_state?: 'active' | 'frozen' | 'none';
  onViewForum: (specialty_id: string) => void;
  onNewThread: (specialty_id: string) => void;
  onViewThread: (thread_id: string) => void;
}

function formatTimeAgo(dateString: string | null | undefined): string {
  if (!dateString) return 'No activity';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

const ForumDiscussionCard: React.FC<ForumDiscussionCardProps> = ({
  specialty_id,
  specialty_name,
  subspecialty_name,
  thread_count,
  member_count,
  last_activity,
  threads,
  index = 0,
  can_enter = true,
  can_post = true,
  access_state = 'active',
  onViewForum,
  onNewThread,
  onViewThread,
}) => {
  const [threadsExpanded, setThreadsExpanded] = useState(false);

  const shade = CARD_BG[index % CARD_BG.length];
  const isLocked = !can_enter;
  const isFrozen = access_state === 'frozen';
  const displayThreads = threadsExpanded ? threads : threads.slice(0, 2);
  const hasMoreThreads = threads.length > 2;
  const latestThread = threads[0];

  return (
    <div
      className={`rounded-xl border transition-all shadow-sm hover:shadow-md min-w-0 overflow-hidden ${shade} ${
        isLocked
          ? 'border-slate-200 opacity-60'
          : isFrozen
          ? 'border-amber-200'
          : 'border-slate-200/80'
      }`}
      data-testid={`forum-card-${specialty_id}`}
    >
      {/* ─── Row 1: Forum Header ─── */}
      <div className="px-5 pt-4 pb-2 min-w-0">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3
                className="text-base font-semibold text-slate-900 cursor-pointer hover:text-sky-700 transition-colors break-words"
                onClick={() => !isLocked && onViewForum(specialty_id)}
                data-testid={`forum-title-${specialty_id}`}
              >
                {specialty_name}
              </h3>
              {subspecialty_name && (
                <Badge variant="outline" className="text-[10px] border-sky-200 text-sky-700 px-1.5 py-0 shrink-0">
                  {subspecialty_name}
                </Badge>
              )}
              {isFrozen && (
                <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200 px-1.5 py-0 shrink-0">
                  <Eye className="h-2.5 w-2.5 mr-0.5" />
                  Read-only
                </Badge>
              )}
              {isLocked && (
                <Badge variant="outline" className="text-[10px] bg-slate-100 text-slate-500 border-slate-200 px-1.5 py-0 shrink-0">
                  <Lock className="h-2.5 w-2.5 mr-0.5" />
                  Locked
                </Badge>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-sm text-slate-500">
              <span className="flex items-center gap-1">
                <MessageSquare className="h-3.5 w-3.5 shrink-0" />
                {thread_count} discussions
              </span>
              <span className="flex items-center gap-1">
                <Users className="h-3.5 w-3.5 shrink-0" />
                {member_count} members
              </span>
              {last_activity && (
                <span className="flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5 shrink-0" />
                  {formatTimeAgo(last_activity)}
                </span>
              )}
            </div>
          </div>

          {!isLocked && can_post && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onNewThread(specialty_id)}
              className="h-8 w-full sm:w-auto px-3 text-xs text-sky-600 border-sky-200 hover:bg-sky-50 shrink-0"
              data-testid={`new-thread-btn-${specialty_id}`}
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              New Discussion
            </Button>
          )}
        </div>
      </div>

      {/* ─── Row 2: Latest Post ─── */}
      {!isLocked && latestThread && (
        <div className="px-5 py-2.5 border-t border-slate-100">
          <button
            onClick={() => setThreadsExpanded(!threadsExpanded)}
            className="flex items-center gap-1.5 mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-sky-500 hover:text-sky-700 transition-colors"
            data-testid={`toggle-threads-${specialty_id}`}
          >
            <Sparkles className="h-3 w-3" /> Latest Discussions
            {hasMoreThreads && (
              threadsExpanded
                ? <ChevronUp className="h-3 w-3" />
                : <ChevronDown className="h-3 w-3" />
            )}
          </button>
        </div>
      )}

      {/* ─── Row 3: Thread List (Collapsible) ─── */}
      {!isLocked && threads.length > 0 && (
        <div className="px-5 pb-2 border-t border-slate-100 bg-sky-50/30">
          <div className="space-y-2 pt-2">
            {displayThreads.map((thread) => (
              <button
                key={thread.thread_id}
                onClick={() => onViewThread(thread.thread_id)}
                className="w-full text-left p-3 rounded-lg border border-slate-100 hover:border-sky-200 hover:bg-white transition-colors"
                data-testid={`thread-item-${thread.thread_id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {thread.is_pinned && (
                        <Badge variant="secondary" className="text-[9px] px-1 py-0">Pinned</Badge>
                      )}
                      <span className="responsive-title font-medium text-slate-900 text-sm line-clamp-1">
                        {thread.title}
                      </span>
                    </div>
                    {thread.preview_comment && (
                      <p className="text-xs text-slate-600 mt-1 line-clamp-2">
                        {thread.preview_comment}
                      </p>
                    )}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-[11px] text-slate-400">
                      <span className="min-w-0">
                        By {thread.creator_name || 'Anonymous'}
                        {thread.creator_is_verified && (
                          <Badge variant="secondary" className="h-3 px-1 text-[8px] font-medium bg-emerald-100 text-emerald-700 border-0 ml-1">
                            Verified
                          </Badge>
                        )}
                      </span>
                      <span className="flex items-center gap-0.5">
                        <MessageSquare className="h-2.5 w-2.5" />
                        {thread.comment_count}
                      </span>
                      <span>{formatTimeAgo(thread.last_activity_at)}</span>
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0 mt-1" />
                </div>
              </button>
            ))}
          </div>

          {/* Show more/less toggle */}
          {hasMoreThreads && (
            <button
              onClick={() => setThreadsExpanded(!threadsExpanded)}
              className="w-full mt-2 py-1.5 text-xs text-sky-600 hover:text-sky-700 font-medium"
              data-testid={`expand-threads-${specialty_id}`}
            >
              {threadsExpanded ? 'Show less' : `Show ${threads.length - 2} more discussions`}
            </button>
          )}
        </div>
      )}

      {/* ─── Empty state for forums with no threads ─── */}
      {!isLocked && threads.length === 0 && (
        <div className="px-5 py-4 border-t border-slate-100 text-center">
          <p className="text-sm text-slate-500">No discussions yet in this forum.</p>
          {can_post && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onNewThread(specialty_id)}
              className="mt-2 h-8 px-3 text-xs text-sky-600 border-sky-200 hover:bg-sky-50"
            >
              Start the first discussion
            </Button>
          )}
        </div>
      )}

      {/* ─── Row 4: View Forum Button ─── */}
      {!isLocked && (
        <div className="flex items-center gap-2 px-5 py-3 border-t border-slate-100 rounded-b-xl">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onViewForum(specialty_id)}
            className="h-8 px-3 text-xs text-sky-600 border-sky-200 hover:bg-sky-50"
            data-testid={`view-forum-btn-${specialty_id}`}
          >
            View All Discussions
            <ChevronRight className="h-3.5 w-3.5 ml-1" />
          </Button>

          {isFrozen && (
            <span className="text-xs text-amber-600 flex items-center gap-1">
              <Eye className="h-3 w-3" />
              Read-only access
            </span>
          )}
        </div>
      )}

      {/* ─── Locked state ─── */}
      {isLocked && (
        <div className="px-5 py-4 border-t border-slate-100 text-center">
          <p className="text-sm text-slate-500">
            Create a digest in this specialty to unlock this forum.
          </p>
        </div>
      )}
    </div>
  );
};

export default ForumDiscussionCard;
